import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type EstadoDoDia } from "../api";
import { obterLocal, ligarCamera, desligarCamera, capturarFoto, contextoSeguro } from "../captura";

/**
 * A TELA DE BATER — um botão, e o dia à vista.
 *
 * O desenho tem uma regra só: quem chega na obra às 7h com o celular na mão
 * precisa bater em um toque. Por isso não há escolha de tipo de marcação — o
 * sistema sabe que a próxima é a entrada, porque as outras três ainda não
 * aconteceram. Escolher "entrada" num menu é o caminho para alguém marcar
 * "saída" às 7h da manhã e o mês fechar errado.
 *
 * A SEQUÊNCIA da batida importa e é sempre a mesma:
 *   1. pede a localização (a mais demorada — começa primeiro);
 *   2. tira a foto;
 *   3. manda para o servidor, que carimba a HORA.
 *
 * A hora do relógio na tela vem do servidor e anda sozinha aqui. Mostrar a hora
 * do celular seria exibir um horário e gravar outro — e o colaborador só
 * descobriria isso no fim do mês, olhando um espelho que não bate com o que ele
 * viu na hora.
 */

interface Props {
  nome: string;
}

/** Diferença entre o relógio do servidor e o do aparelho, em ms. */
function calcularDefasagem(servidorIso: string): number {
  return new Date(servidorIso).getTime() - Date.now();
}

function horaDe(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** "2026-09-15" -> "terça-feira, 15 de setembro". */
function dataPorExtenso(iso: string): string {
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  const semana = [
    "domingo", "segunda-feira", "terça-feira", "quarta-feira",
    "quinta-feira", "sexta-feira", "sábado",
  ];
  const [ano, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(ano, m - 1, d));
  return `${semana[dt.getUTCDay()]}, ${d} de ${meses[m - 1]}`;
}

type Etapa = "parado" | "localizando" | "camera" | "enviando";

export default function BaterPonto({ nome }: Props) {
  const [estado, setEstado] = useState<EstadoDoDia | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<string | null>(null);
  const [etapa, setEtapa] = useState<Etapa>("parado");
  const [relogio, setRelogio] = useState("--:--:--");

  const defasagemRef = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // O local é obtido ANTES da foto e guardado aqui: o GPS é a parte lenta, e
  // pedi-lo depois faria a pessoa posar para a câmera esperando o sinal.
  const localRef = useRef<{ latitude: number; longitude: number; precisao_m: number } | null>(null);

  const carregar = useCallback(async () => {
    try {
      const e = await api.hoje();
      setEstado(e);
      defasagemRef.current = calcularDefasagem(e.servidor_em);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar o dia.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O relógio anda pelo horário DO SERVIDOR: o do aparelho só entra como base,
  // corrigida pela defasagem medida na última resposta.
  useEffect(() => {
    const t = setInterval(() => setRelogio(horaDe(Date.now() + defasagemRef.current)), 1000);
    return () => clearInterval(t);
  }, []);

  // A câmera não pode ficar ligada depois que a tela sai do ar.
  useEffect(() => () => desligarCamera(streamRef.current), []);

  /** Passo 1: localização, depois abre a câmera. */
  async function iniciar() {
    if (!estado) return;
    setErro(null);
    setConfirmacao(null);
    localRef.current = null;

    try {
      if (estado.regras.exigir_gps) {
        setEtapa("localizando");
        localRef.current = await obterLocal();
      }

      if (estado.regras.exigir_foto) {
        setEtapa("camera");
        const stream = await ligarCamera();
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {
            /* autoplay barrado: o vídeo aparece no primeiro toque */
          });
        }
        return; // espera o toque em "Registrar"
      }

      // Sem exigência de foto, a batida sai direto.
      await enviar(null);
    } catch (err) {
      setEtapa("parado");
      setErro(err instanceof Error ? err.message : "Não foi possível preparar a batida.");
    }
  }

  /** Passo 2: captura o quadro e envia. */
  async function registrar() {
    if (!videoRef.current) return;
    try {
      const foto = capturarFoto(videoRef.current);
      await enviar(foto);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível tirar a foto.");
      setEtapa("parado");
      desligarCamera(streamRef.current);
      streamRef.current = null;
    }
  }

  async function enviar(foto: string | null) {
    setEtapa("enviando");
    setErro(null);
    try {
      const r = await api.bater({
        latitude: localRef.current?.latitude,
        longitude: localRef.current?.longitude,
        precisao_m: localRef.current?.precisao_m,
        foto: foto ?? undefined,
      });
      setConfirmacao(`${r.rotulo} registrada às ${r.hora}.`);
      // Recarrega tudo em vez de remendar o estado local: assim o que a tela
      // mostra é o que o servidor tem, e não o que a tela achou que gravou.
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível registrar. Tente de novo.");
    } finally {
      setEtapa("parado");
      desligarCamera(streamRef.current);
      streamRef.current = null;
      localRef.current = null;
    }
  }

  function cancelar() {
    desligarCamera(streamRef.current);
    streamRef.current = null;
    localRef.current = null;
    setEtapa("parado");
  }

  if (!estado) {
    return <div className="carregando">{erro ?? "Carregando…"}</div>;
  }

  const ordem = ["entrada", "saida_almoco", "retorno_almoco", "saida"];
  const completo = !estado.proximo;
  const primeiroNome = nome.trim().split(/\s+/)[0];

  return (
    <div className="bater">
      {/* Aviso que vale mais que qualquer mensagem de erro depois: sem https,
          nem a câmera nem o GPS funcionam, e o motivo não é óbvio para ninguém. */}
      {!contextoSeguro() && (estado.regras.exigir_gps || estado.regras.exigir_foto) && (
        <div className="aviso-erro">
          Este endereço não é seguro (https), então a câmera e a localização ficam bloqueadas pelo
          navegador. Abra o ponto pelo endereço oficial da empresa.
        </div>
      )}

      <header className="bater-topo">
        <p className="bater-saudacao">Olá, {primeiroNome}</p>
        <p className="bater-data">{dataPorExtenso(estado.data)}</p>
        <div className="bater-relogio" aria-label="Horário do servidor">
          {relogio}
        </div>
        <p className="bater-fonte">horário do servidor da empresa</p>
      </header>

      {confirmacao && <div className="aviso-ok">{confirmacao}</div>}
      {erro && <div className="aviso-erro">{erro}</div>}

      {etapa === "camera" ? (
        <div className="camera">
          <video ref={videoRef} playsInline muted autoPlay className="camera-video" />
          <p className="camera-dica">Enquadre o rosto e toque em registrar.</p>
          <button className="botao-principal" onClick={registrar}>
            Registrar {estado.rotulos[estado.proximo ?? ""] ?? "ponto"}
          </button>
          <button className="botao-texto" onClick={cancelar}>
            Cancelar
          </button>
        </div>
      ) : completo ? (
        <div className="bater-completo">
          <p className="bater-completo-titulo">Dia registrado</p>
          <p>As quatro marcações de hoje já foram feitas.</p>
        </div>
      ) : (
        <button
          className="botao-bater"
          onClick={iniciar}
          disabled={etapa !== "parado"}
        >
          <span className="botao-bater-acao">
            {etapa === "localizando"
              ? "Obtendo localização…"
              : etapa === "enviando"
                ? "Registrando…"
                : "Bater ponto"}
          </span>
          {etapa === "parado" && (
            <span className="botao-bater-tipo">{estado.rotulos[estado.proximo ?? ""]}</span>
          )}
        </button>
      )}

      <section className="marcacoes">
        <h2 className="marcacoes-titulo">Hoje</h2>
        <ul className="marcacoes-lista">
          {ordem.map((tipo) => {
            const feita = estado.batidas[tipo];
            const proxima = estado.proximo === tipo;
            return (
              <li
                key={tipo}
                className={`marcacao ${feita ? "marcacao-feita" : ""} ${proxima ? "marcacao-proxima" : ""}`}
              >
                <span className="marcacao-nome">{estado.rotulos[tipo]}</span>
                <span className="marcacao-hora">
                  {feita ? feita.hora : proxima ? "a registrar" : "—"}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
